import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Tag } from 'lucide-react';
import LazyImage from './LazyImage';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featuredImage: string;
  publishedAt: string;
  readTime: string;
  tags: string[];
  author: string;
}

const BlogSection: React.FC = () => {
  useEffect(() => {
    // Set page title for blog section
    document.title = "Blog | DJ Landan Time - House Music Insights & Stories";
    
    // Set canonical URL for blog page
    const canonicalUrl = 'https://djlandantime.co.uk/blog';
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
    
    // Set meta description for blog page
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Insights from three decades in the music industry, stories from the underground scene, and everything in between. Read DJ Landan Time\'s blog about house music, technology, and the music industry.');
    
    // Set Open Graph tags for blog page
    const ogTags = [
      { property: 'og:title', content: 'Blog | DJ Landan Time - House Music Insights & Stories' },
      { property: 'og:description', content: 'Insights from three decades in the music industry, stories from the underground scene, and everything in between.' },
      { property: 'og:url', content: canonicalUrl },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://ik.imagekit.io/bckqiai0e/blog%20posts%20picture%20dj%20landan%20time.png?updatedAt=1749389811996' }
    ];
    
    ogTags.forEach(tag => {
      let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', tag.property);
        document.head.appendChild(ogTag);
      }
      ogTag.setAttribute('content', tag.content);
    });
    
    // Cleanup function
    return () => {
      document.title = "DJ Landan Time | London House Music DJ & Producer";
      
      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        canonicalLink.setAttribute('href', 'https://djlandantime.co.uk');
      }
      
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', "DJ Landan Time is an independent UK-based music producer, song writer, DJ, and remixer whose career is rooted in decades of experience behind the decks and a deep passion for dance music culture. Known for his versatility, he produces across all styles of house music — from deep and soulful to tech, big room, and festival-driven sounds — while also drawing on hip-hop, pop, and cinematic influences.");
      }
    };
  }, []);

  const blogPosts: BlogPost[] = [
    {
      id: '1',
      title: 'Everything Comes From the Blues: How the Blues Shaped Pop, House & Dance Music',
      slug: 'everything-comes-from-blues-shaped-pop-house-dance-music',
      excerpt: 'Exploring the deep roots of modern music and how the blues continues to influence everything from house music to hip-hop, disco to EDM.',
      featuredImage: 'https://ik.imagekit.io/bckqiai0e/DJ%20Landan%20Time%20-%20Rap,Disco,Rock%20&%20Roll%20-%201400%20jpeg.jpg?updatedAt=1751219651790',
      publishedAt: '2025-01-20',
      readTime: '6 min read',
      tags: ['Blues', 'Music History', 'House Music'],
      author: 'DJ Landan Time'
    },
    {
      id: '2',
      title: 'The Evolution of House Music: From Underground to Mainstream',
      slug: 'evolution-house-music-underground-mainstream',
      excerpt: 'Exploring how house music transformed from Chicago warehouses to global dancefloors, and what this means for the future of electronic music.',
      featuredImage: 'https://ik.imagekit.io/bckqiai0e/blog%20posts%20picture%20dj%20landan%20time.png?updatedAt=1749389811996',
      publishedAt: '2025-01-18',
      readTime: '8 min read',
      tags: ['House Music', 'Music History', 'Electronic Music'],
      author: 'DJ Landan Time'
    },
    {
      id: '3',
      title: 'The Artificially Intelligent Impact on Music Creation',
      slug: 'artificially-intelligent-impact-music-creation',
      excerpt: 'Exploring how AI is revolutionizing music production, from composition to mastering, and what it means for artists and the future of creativity.',
      featuredImage: 'https://ik.imagekit.io/bckqiai0e/AI.png?updatedAt=1749390234176',
      publishedAt: '2025-01-15',
      readTime: '10 min read',
      tags: ['AI', 'Music Technology', 'Future of Music', 'Production'],
      author: 'DJ Landan Time'
    },
    {
      id: '4',
      title: 'The Rise of Independent Music: How Artists Are Taking Control',
      slug: 'rise-independent-music-artists-taking-control',
      excerpt: 'Exploring how independent artists are revolutionizing the music industry through digital platforms, direct fan engagement, and creative freedom.',
      featuredImage: 'https://ik.imagekit.io/bckqiai0e/ChatGPT%20Image%20Jun%208,%202025,%2002_46_57%20PM.png?updatedAt=1749390447894',
      publishedAt: '2025-01-10',
      readTime: '7 min read',
      tags: ['Independent Music', 'Music Industry', 'Digital Platforms', 'Artist Rights'],
      author: 'DJ Landan Time'
    }
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.warn('Image failed to load:', e.currentTarget.src);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleBlogPostClick = () => {
    // Scroll to top when navigating to blog post
    window.scrollTo(0, 0);
  };

  return (
    <section id="blog" className="py-24 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[url('https://ik.imagekit.io/bckqiai0e/background-waves-2.png')] bg-cover bg-center opacity-30"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-950/10 to-black"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-4">Blog</h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Insights from three decades in the music industry, stories from the underground scene, and everything in between.
          </p>
        </div>

        {/* Featured Post */}
        <div className="mb-16">
          <Link 
            to={`/blog/${blogPosts[0].slug}`}
            onClick={handleBlogPostClick}
            className="group block bg-gray-900/30 rounded-2xl border border-purple-900/30 overflow-hidden hover:border-purple-500/50 transition-all duration-300"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <div className="relative aspect-video lg:aspect-square">
                <LazyImage
                  src={blogPosts[0].featuredImage}
                  alt={blogPosts[0].title}
                  className="w-full h-full object-cover"
                  onError={handleImageError}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Featured
                  </span>
                </div>
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(blogPosts[0].publishedAt)}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {blogPosts[0].readTime}
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-white mb-4 group-hover:text-purple-300 transition-colors">
                  {blogPosts[0].title}
                </h2>
                <p className="text-gray-400 mb-6 line-clamp-3">
                  {blogPosts[0].excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {blogPosts[0].tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="bg-purple-900/30 text-purple-300 px-2 py-1 rounded text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center text-purple-400 group-hover:text-purple-300 transition-colors">
                    <span className="mr-2">Read More</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.slice(1).map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              onClick={handleBlogPostClick}
              className="group block bg-gray-900/30 rounded-xl border border-purple-900/30 overflow-hidden hover:border-purple-500/50 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="relative aspect-video">
                <LazyImage
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover"
                  onError={handleImageError}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(post.publishedAt)}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {post.readTime}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-white mb-3 group-hover:text-purple-300 transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-1">
                    {post.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="bg-purple-900/30 text-purple-300 px-2 py-1 rounded text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <ArrowRight className="h-4 w-4 text-purple-400 group-hover:text-purple-300 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Load More Button */}
        <div className="text-center mt-12">
          <button className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-purple-500/30 transition-all duration-300">
            Load More Posts
          </button>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;